import type { CollectionState, ComparableArtist, GraphVenue } from "@/api/discover";

export type EvidenceStatus = {
  label: string;
  tone: "ok" | "muted" | "warn" | "error" | "busy";
  /** Primary evidence action for this state, or null when an update isn't useful. */
  action: "find" | "refresh" | null;
};

/** Status for a comparable card, derived from stored state (no provider call). */
export function evidenceStatus(comparable: ComparableArtist): EvidenceStatus {
  const h = comparable.hydration;
  switch (h?.state) {
    case "running": return { label: h.evidence_available ? "Updating touring history…" : "Gathering touring history…", tone: "busy", action: null };
    case "commissioned": return { label: "Touring history update queued", tone: "muted", action: null };
    case "requested": return { label: h.evidence_available ? "Touring history update requested" : "Touring history requested", tone: "muted", action: null };
    case "review_required": return { label: "Touring history review pending", tone: "muted", action: null };
    case "failed": return { label: "Touring history update temporarily unavailable", tone: "warn", action: null };
    case "deferred": return { label: "Touring history update deferred", tone: "muted", action: null };
    case "succeeded": return { label: "Touring history checked", tone: "ok", action: null };
  }
  const available = (comparable.evidence?.shows ?? 0) > 0;
  return available
    ? { label: "Touring history available", tone: "ok", action: null }
    : { label: "No touring history available yet", tone: "muted", action: null };
}

/** Message shown right after the user explicitly asked for an update. */
export function collectionResultMessage(result: CollectionState): { text: string; tone: EvidenceStatus["tone"] } {
  switch (result) {
    case "collected":
      return { text: "Updated", tone: "ok" };
    case "fresh":
      return { text: "Already up to date", tone: "ok" };
    case "in_progress":
      return { text: "Updating… check back in a moment", tone: "busy" };
    case "cooldown":
      return { text: "Recently checked — try again later", tone: "muted" };
    case "empty":
      return { text: "No performance history found yet", tone: "muted" };
    case "failed":
      return { text: "Couldn't update right now", tone: "error" };
    case "missing_identifier":
      return { text: "Live history isn't available for this artist", tone: "muted" };
    default:
      return { text: "Couldn't update right now", tone: "error" };
  }
}

export const PLAN_REQUIRED_MESSAGE =
  "Your free trial has ended. Choose a plan in Settings → Billing to keep using this.";

export function isPlanRequired(error: unknown): boolean {
  const err = error as {
    code?: string;
    payload?: { error?: string; detail?: string | { error?: string } };
  } | null;
  const detail = err?.payload?.detail;
  return (
    err?.code === "capability_required" ||
    err?.payload?.error === "requires_pro" ||
    (typeof detail === "object" && detail?.error === "requires_pro")
  );
}

export function friendlyError(error: unknown, fallback: string): string {
  const status = (error as { status?: number } | null)?.status;
  if (isPlanRequired(error)) return PLAN_REQUIRED_MESSAGE;
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have access to do that for this artist.";
  if (status === 404) return "That item is no longer available.";
  if ((error as { code?: string } | null)?.code === "timeout") return "This is taking longer than expected. Please try again.";
  return fallback;
}

const MONTH_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
const DAY_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatMonth(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? MONTH_FORMAT.format(date) : "—";
}

export function formatDay(value: string | null | undefined): string {
  const date = parseDate(value);
  return date ? DAY_FORMAT.format(date) : "—";
}

export function formatLocation(parts: (string | null | undefined)[]): string {
  return parts.filter((part) => part && part.trim()).join(", ");
}

const DAY_MS = 24 * 60 * 60 * 1000;
const utcDay = (date: Date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * Deterministic, text-only contact recency (UTC calendar days, like the other Discover dates):
 * today, yesterday, N days (<7), N weeks (<30 days), N months (<365 days), then "Mon YYYY".
 */
export function formatContactedRecency(value: string | null | undefined, now: Date = new Date()): string {
  const date = parseDate(value);
  if (!date) return "Contacted";
  const days = Math.floor((utcDay(now) - utcDay(date)) / DAY_MS);
  if (days <= 0) return "Contacted today";
  if (days === 1) return "Contacted yesterday";
  if (days < 7) return `Contacted ${plural(days, "day")} ago`;
  if (days < 30) return `Contacted ${plural(Math.floor(days / 7), "week")} ago`;
  if (days < 365) return `Contacted ${plural(Math.max(1, Math.floor(days / 30)), "month")} ago`;
  return `Contacted ${MONTH_FORMAT.format(date)}`;
}

/** "Why this venue": each comparable artist's most recent appearance, newest first. */
export function latestAppearanceByArtist(venue: GraphVenue | null): GraphVenue["events"] {
  if (!venue) return [];
  const latest = new Map<number, GraphVenue["events"][number]>();
  for (const event of venue.events) {
    const current = latest.get(event.public_artist_id);
    if (!current || event.event_date > current.event_date) latest.set(event.public_artist_id, event);
  }
  return [...latest.values()].sort((a, b) => b.event_date.localeCompare(a.event_date));
}
